class RSAController {
  constructor() {
    this.serverPublicKey = null;
    this.userKeyPair = null;
    this.currentUserEmail = null;
    this.STORAGE_KEYS = {
      PRIVATE_KEY: 'rsa_user_private_key',
      PUBLIC_KEY: 'rsa_user_public_key',
      SERVER_PUBLIC_KEY: 'rsa_server_public_key',
      CURRENT_USER: 'rsa_current_user_email'
    };
  }

  setCurrentUser(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    const storedUserEmail = localStorage.getItem(this.STORAGE_KEYS.CURRENT_USER);
    
    if (storedUserEmail && storedUserEmail !== email) {
      this.clearUserKeys(storedUserEmail);
    }

    this.currentUserEmail = email;
    localStorage.setItem(this.STORAGE_KEYS.CURRENT_USER, email);
  }

  #getUserStorageKeys() {
    return {
      PRIVATE_KEY: this.STORAGE_KEYS.PRIVATE_KEY,
      PUBLIC_KEY: this.STORAGE_KEYS.PUBLIC_KEY
    };
  }

  #readStorage(key) {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  #writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
    try {
      localStorage.setItem(key, value);
    } catch (_) {}
  }

  #removeStorage(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {}
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  }

  clearUserKeys(email = null) {
    const userKeys = this.#getUserStorageKeys();
    this.#removeStorage(userKeys.PRIVATE_KEY);
    this.#removeStorage(userKeys.PUBLIC_KEY);
    
    if (!email || email === this.currentUserEmail) {
      this.currentUserEmail = null;
      this.userKeyPair = null;
      this.#removeStorage(this.STORAGE_KEYS.CURRENT_USER);
    }
  }

  clearServerPublicKey() {
    this.serverPublicKey = null;
    this._oaepMaxPlaintextBytes = null;
    this.#removeStorage(this.STORAGE_KEYS.SERVER_PUBLIC_KEY);
  }

  async #storeUserKeys() {
    if (!this.userKeyPair) {
      throw new Error('No key pair to store');
    }

    const publicKeyJWK = await crypto.subtle.exportKey('jwk', this.userKeyPair.publicKey);
    const privateKeyJWK = await crypto.subtle.exportKey('jwk', this.userKeyPair.privateKey);
    
    const userKeys = this.#getUserStorageKeys();
    this.#writeStorage(userKeys.PUBLIC_KEY, JSON.stringify(publicKeyJWK));
    this.#writeStorage(userKeys.PRIVATE_KEY, JSON.stringify(privateKeyJWK));
  }

  async #loadUserKeyPair() {
    try {
      const userKeys = this.#getUserStorageKeys();
      const publicKeyJWK = this.#readStorage(userKeys.PUBLIC_KEY);
      const privateKeyJWK = this.#readStorage(userKeys.PRIVATE_KEY);
      
      if (!publicKeyJWK || !privateKeyJWK) {
        throw new Error('No keys found in storage');
      }
      
      this.userKeyPair = {
        publicKey: await crypto.subtle.importKey(
          'jwk',
          JSON.parse(publicKeyJWK),
          {
            name: 'RSA-OAEP',
            hash: 'SHA-256'
          },
          true,
          ['encrypt']
        ),
        privateKey: await crypto.subtle.importKey(
          'jwk',
          JSON.parse(privateKeyJWK),
          {
            name: 'RSA-OAEP',
            hash: 'SHA-256'
          },
          true,
          ['decrypt']
        )
      };
      return this.userKeyPair;
    } catch (error) {
      throw new Error('Failed to load user key pair');
    }
  }

  async #initializeUserKeys() {
    const userKeys = this.#getUserStorageKeys();
    const hasKeys = this.#readStorage(userKeys.PRIVATE_KEY);
    
    if (hasKeys) {
      return await this.#loadUserKeyPair();
    } else {
      return await this.#generateUserKeyPair();
    }
  }

  async #generateUserKeyPair() {
    try {
      this.userKeyPair = await crypto.subtle.generateKey(
        {
          name: 'RSA-OAEP',
          modulusLength: 4096,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-256'
        },
        true,
        ['encrypt', 'decrypt']
      );
      await this.#storeUserKeys();
      return this.userKeyPair;
    } catch (error) {
      throw new Error('Failed to generate user key pair');
    }
  }

  async #setServerPublicKey(keyData, format = 'pem') {
    try {
      let keyObject;

      if (format === 'pem') {
        const pemHeader = '-----BEGIN PUBLIC KEY-----';
        const pemFooter = '-----END PUBLIC KEY-----';
        const pemContents = keyData
          .replace(pemHeader, '')
          .replace(pemFooter, '')
          .replace(/\s/g, '');
        
        const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));
        keyObject = binaryDer.buffer;
      } else if (format === 'jwk') {
        keyObject = typeof keyData === 'string' ? JSON.parse(keyData) : keyData;
      } else {
        throw new Error('Unsupported key format');
      }

      this._oaepMaxPlaintextBytes = null;
      // Public key is extractable so the OAEP block size can be read from the modulus.
      this.serverPublicKey = await crypto.subtle.importKey(
        format === 'pem' ? 'spki' : 'jwk',
        keyObject,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-1'
        },
        true,
        ['encrypt']
      );

      if (format === 'jwk') {
        this.#writeStorage(
          this.STORAGE_KEYS.SERVER_PUBLIC_KEY,
          JSON.stringify(keyObject)
        );
      } else {
        this.#writeStorage(
          this.STORAGE_KEYS.SERVER_PUBLIC_KEY,
          keyData
        );
      }
    } catch (error) {
      throw new Error('Failed to set server public key');
    }
  }

  async #getServerPublicKey() {
    if (this.serverPublicKey) {
      return this.serverPublicKey;
    }

    const stored = this.#readStorage(this.STORAGE_KEYS.SERVER_PUBLIC_KEY);
    
    if (stored) {
      try {
        const jwk = JSON.parse(stored);
        await this.#setServerPublicKey(jwk, 'jwk');
      } catch {
        await this.#setServerPublicKey(stored, 'pem');
      }
      return this.serverPublicKey;
    }

    throw new Error('Server public key not found. Call fetchServerPublicKey() first.');
  }

  async generateUserKeyPair() {
    return await this.#generateUserKeyPair();
  }

  async getUserPublicKey(email = null) {
    if (email) {
      this.setCurrentUser(email);
    }

    if (!this.userKeyPair) {
      await this.#initializeUserKeys();
    }
    
    try {
      const exported = await crypto.subtle.exportKey('spki', this.userKeyPair.publicKey);
      const base64 = btoa(String.fromCharCode(...new Uint8Array(exported)));
      return `-----BEGIN PUBLIC KEY-----\n${base64}\n-----END PUBLIC KEY-----`;
    } catch (error) {
      if (error.name === 'InvalidAccessError' || error.message.includes('not extractable')) {
        const userKeys = this.#getUserStorageKeys();
        this.#removeStorage(userKeys.PRIVATE_KEY);
        this.#removeStorage(userKeys.PUBLIC_KEY);
        await this.#generateUserKeyPair();
        const exported = await crypto.subtle.exportKey('spki', this.userKeyPair.publicKey);
        const base64 = btoa(String.fromCharCode(...new Uint8Array(exported)));
        return `-----BEGIN PUBLIC KEY-----\n${base64}\n-----END PUBLIC KEY-----`;
      }
      throw error;
    }
  }

  async getUserPrivateKey() {
    if (!this.userKeyPair) {
      await this.#initializeUserKeys();
    }
    return this.userKeyPair.privateKey;
  }

  async getUserPrivateKeyPEM() {
    try {
      if (!this.userKeyPair) {
        await this.#initializeUserKeys();
      }

      const exported = await crypto.subtle.exportKey('pkcs8', this.userKeyPair.privateKey);
      const exportedArray = new Uint8Array(exported);
      const base64 = btoa(String.fromCharCode(...exportedArray));
      const pem = `-----BEGIN PRIVATE KEY-----\n${base64.match(/.{1,64}/g).join('\n')}\n-----END PRIVATE KEY-----`;
      
      return pem;
    } catch (error) {
      throw error;
    }
  }

  async getServerPublicKeyPEM() {
    try {
      const stored = this.#readStorage(this.STORAGE_KEYS.SERVER_PUBLIC_KEY);
      if (stored && stored.includes('BEGIN PUBLIC KEY')) {
        return stored;
      }
      
      const serverKey = await this.#getServerPublicKey();
      const exported = await crypto.subtle.exportKey('spki', serverKey);
      const base64 = btoa(String.fromCharCode(...new Uint8Array(exported)));
      return `-----BEGIN PUBLIC KEY-----\n${base64}\n-----END PUBLIC KEY-----`;
    } catch (error) {
      throw new Error('Server public key not available. Make sure fetchAndStoreServerPublicKey() has been called first.');
    }
  }

  #decodeBase64Url(value) {
    const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
    const pad = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
    const binary = atob(normalized + pad);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Largest plaintext OpenSSL will decrypt with OPENSSL_PKCS1_OAEP_PADDING (SHA-1).
   * API server keys are 4096-bit, so this is 470 bytes — not 256.
   */
  async #oaepMaxPlaintextBytes() {
    if (this._oaepMaxPlaintextBytes) return this._oaepMaxPlaintextBytes;
    const overhead = 42;
    let modulusBytes = 512;
    try {
      const serverKey = await this.#getServerPublicKey();
      let jwk;
      try {
        jwk = await crypto.subtle.exportKey('jwk', serverKey);
      } catch (_) {
        const stored = this.#readStorage(this.STORAGE_KEYS.SERVER_PUBLIC_KEY);
        if (!stored) throw new Error('Server public key is not extractable');
        try {
          await this.#setServerPublicKey(JSON.parse(stored), 'jwk');
        } catch {
          await this.#setServerPublicKey(stored, 'pem');
        }
        jwk = await crypto.subtle.exportKey('jwk', this.serverPublicKey);
      }
      const raw = this.#decodeBase64Url(jwk.n);
      modulusBytes = raw.length;
      if (modulusBytes > 1 && raw[0] === 0x00) modulusBytes -= 1;
    } catch (_) {
      modulusBytes = 512;
    }
    const max = modulusBytes - overhead;
    this._oaepMaxPlaintextBytes = max > 16 ? max : 214;
    return this._oaepMaxPlaintextBytes;
  }

  async encryptWithServerKey(data) {
    try {
      const serverKey = await this.#getServerPublicKey();
      const dataBuffer = data instanceof Uint8Array
        ? data
        : new TextEncoder().encode(String(data));
      const maxSize = await this.#oaepMaxPlaintextBytes();

      if (dataBuffer.length > maxSize) {
        throw new Error(
          `Data too large for RSA encryption. Max size: ${maxSize} bytes. Data size: ${dataBuffer.length} bytes.`
        );
      }

      const encrypted = await crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP',
        },
        serverKey,
        dataBuffer
      );

      return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
    } catch (error) {
      if (String(error?.message || '').startsWith('Failed to encrypt data')) {
        throw error;
      }
      throw new Error(`Failed to encrypt data: ${error.message}`);
    }
  }

  /**
   * Encrypt a JSON payload in OAEP-sized byte blocks.
   * Blocks stay as raw bytes so a multi-byte character on the cut is not
   * re-encoded into a larger chunk.
   * @returns {Promise<string[]>}
   */
  async encryptJsonForServer(value) {
    const json = typeof value === 'string' ? value : JSON.stringify(value);
    const bytes = new TextEncoder().encode(json);
    const maxSize = await this.#oaepMaxPlaintextBytes();
    if (bytes.length === 0) {
      return [await this.encryptWithServerKey(bytes)];
    }
    const segments = [];
    for (let offset = 0; offset < bytes.length; offset += maxSize) {
      const end = Math.min(offset + maxSize, bytes.length);
      segments.push(await this.encryptWithServerKey(bytes.subarray(offset, end)));
    }
    return segments;
  }

  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async importPrivateKeyWithHash(pem, hashAlgo) {
    const pemHeader = "-----BEGIN PRIVATE KEY-----";
    const pemFooter = "-----END PRIVATE KEY-----";
    const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length);
    const binaryDer = this.base64ToArrayBuffer(pemContents.replace(/\s/g, ''));
    
    return await crypto.subtle.importKey(
      "pkcs8",
      binaryDer,
      {
        name: "RSA-OAEP",
        hash: hashAlgo
      },
      false,
      ["decrypt"]
    );
  }

  async tryDecryptWithHash(encryptedBase64, hashAlgo) {
    try {
      const privateKeyPEM = await this.getUserPrivateKeyPEM();
      const privateKey = await this.importPrivateKeyWithHash(privateKeyPEM, hashAlgo);
      const encryptedBuffer = this.base64ToArrayBuffer(encryptedBase64);

      const decryptedBuffer = await crypto.subtle.decrypt(
        { 
          name: "RSA-OAEP"
        },
        privateKey,
        encryptedBuffer
      );

      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      return null;
    }
  }

  async decryptWithUserKey(encryptedData) { 
    try {
      if (!this.userKeyPair) {
        await this.#initializeUserKeys();
      }
      
      const hashAlgorithms = ['SHA-256', 'SHA-1', 'SHA-384', 'SHA-512'];
      let decryptedData = null;

      for (const hashAlgo of hashAlgorithms) {
        decryptedData = await this.tryDecryptWithHash(encryptedData, hashAlgo);
        if (decryptedData) {
          break;
        }
      }

      if (decryptedData) {
        return decryptedData;
      } else {
        throw new Error('Failed to decrypt with all hash algorithms (SHA-256, SHA-1, SHA-384, SHA-512)');
      }
    } catch (error) {
      throw new Error('Failed to decrypt data');
    }
  }

  async setServerPublicKeyFromResponse(keyData, format = 'pem') {
    return await this.#setServerPublicKey(keyData, format);
  }
}

export default new RSAController();
